import React, { useState } from 'react';
import { BaseModal, ClientCombobox } from '@/components/shared';
import type {
  TipoFluxoMovimentacao,
  TipoEquipamentoMovimentacao,
  SistemaOperacionalMovimentacao,
} from '../../../models';
import { useEquipamentosDoColaboradorQuery, useMovimentacaoMutations } from '../../../hooks/data';
import { itemOutroEquipamentoDevolucao, itensIntakePorEquipamentos } from '../../../utils/equipamentosDevolucao';
import ListaEquipamentosColaborador from '../../NovaSolicitacaoPage/components/ListaEquipamentosColaborador';
import { dataInputParaDate } from '../../../utils/dataInput';
import {
  TIPO_FLUXO_LABELS,
  TIPO_EQUIPAMENTO_LABELS,
  SISTEMA_OPERACIONAL_LABELS,
  SO_OPCOES_POR_TIPO_EQUIPAMENTO,
  SO_IMPLICITO_POR_TIPO_EQUIPAMENTO,
  TIPOS_EQUIPAMENTO_COM_HOMOLOGACAO,
} from '../solicitacaoDetailsPage.definitions';

interface NovaMovimentacaoModalProps {
  solicitacaoId: string;
  isOpen: boolean;
  onClose: () => void;
  clienteNome: string | null;
  /** Cliente da Solicitação — pré-seleciona o combobox, mas continua editável (uma
   * Movimentação pode ser de um Cliente diferente do resto, ver Movimentacao.clienteId). */
  clienteId: string | null;
  /** Ver SolicitacaoDetailsContent — só alguns Clientes permitem equipamento próprio. */
  propriedadeVisivel: boolean;
  /** Ver SolicitacaoDetailsContent — só alguns Clientes usam máquina homologada. */
  homologadoVisivel: boolean;
  /** Devolução: a lista dos equipamentos que estão com o colaborador (null na Admissão sem vínculo). */
  colaboradorId: string | null;
  /** Alocação padrão da nova Movimentação (a da Solicitação) — o SI troca depois no card. */
  alocacaoReferenciadaId: string | null;
}

/**
 * Criação de uma nova Movimentação — exclusiva do SI (Discovery §10.2). Usada pra 2ª+
 * Movimentação de uma Solicitação (a 1ª nasce junto da Solicitação, ver NovaSolicitacaoPage).
 *
 * Envio: tipo/SO/homologado/propriedade do equipamento que vai. Devolução: os mesmos campos
 * da Nova Solicitação de devolução — escolhe entre os equipamentos que estão com a pessoa (ou
 * descreve um que não está na lista), sem perguntar tipo/SO de novo (feedback do SI).
 */
export default function NovaMovimentacaoModal({
  solicitacaoId,
  isOpen,
  onClose,
  clienteNome,
  clienteId,
  propriedadeVisivel,
  homologadoVisivel: permiteHomologado,
  colaboradorId,
  alocacaoReferenciadaId,
}: NovaMovimentacaoModalProps) {
  const { criarMovimentacao } = useMovimentacaoMutations(solicitacaoId);

  const [tipoFluxo, setTipoFluxo] = useState<TipoFluxoMovimentacao>('envio');
  const [cliente, setCliente] = useState<{ id: string; name: string } | null>(
    () => (clienteId ? { id: clienteId, name: clienteNome ?? '' } : null)
  );
  const [snapshotTipoEquipamento, setSnapshotTipoEquipamento] = useState<TipoEquipamentoMovimentacao>('notebook');
  const [sistemaOperacional, setSistemaOperacional] = useState<SistemaOperacionalMovimentacao | ''>('');
  const [homologado, setHomologado] = useState(false);
  const [dataPrevista, setDataPrevista] = useState('');
  const [escolhidosIds, setEscolhidosIds] = useState<Set<string>>(new Set());
  const [outroEquipamento, setOutroEquipamento] = useState('');
  const ehDevolucao = tipoFluxo === 'devolucao';
  const { data: equipamentosColaborador = [] } = useEquipamentosDoColaboradorQuery(
    isOpen && ehDevolucao ? (colaboradorId ?? undefined) : undefined
  );
  const equipamentosParaDevolver =
    equipamentosColaborador.length === 1
      ? equipamentosColaborador
      : equipamentosColaborador.filter((equipamento) => escolhidosIds.has(equipamento.id));

  const soOpcoes = SO_OPCOES_POR_TIPO_EQUIPAMENTO[snapshotTipoEquipamento];
  const soVisivel = Boolean(soOpcoes);
  const soImplicito = SO_IMPLICITO_POR_TIPO_EQUIPAMENTO[snapshotTipoEquipamento];
  const homologadoVisivel = permiteHomologado && TIPOS_EQUIPAMENTO_COM_HOMOLOGACAO.includes(snapshotTipoEquipamento);

  const podeSubmeter = ehDevolucao
    ? Boolean(equipamentosParaDevolver.length > 0 || outroEquipamento.trim())
    : Boolean(snapshotTipoEquipamento && (!soVisivel || sistemaOperacional));

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!podeSubmeter) return;

    await criarMovimentacao.mutateAsync({
      tipoFluxo,
      clienteId: cliente?.id ?? null,
      // Nasce com 1 equipamento — o SI pode adicionar mais depois na tela de detalhes
      // (uma Movimentação pode ter vários, ex. notebook + monitor no mesmo envio).
      equipamentosIntake: ehDevolucao
        ? [...itensIntakePorEquipamentos(equipamentosParaDevolver), ...itemOutroEquipamentoDevolucao(outroEquipamento)]
        : [
            {
              id: crypto.randomUUID(),
              tipoEquipamento: snapshotTipoEquipamento,
              sistemaOperacional: soVisivel ? (sistemaOperacional as SistemaOperacionalMovimentacao) : soImplicito ?? null,
              homologado: homologadoVisivel ? homologado : null,
              // Vem do Inventário ao vincular o patrimônio (ver useVincularEquipamentoMutation).
              propriedadeEquipamento: propriedadeVisivel ? null : 'raro',
            },
          ],
      // Opcional: o SI pode deixar em branco e preencher depois.
      dataPrevista: dataPrevista ? (dataInputParaDate(dataPrevista) as any) : null,
      alocacaoReferenciadaId,
    });

    onClose();
  };

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      title="Nova Movimentação"
      maxWidth="max-w-lg"
      fullHeight={false}
      actions={
        <div className="flex justify-end gap-3">
          <button type="button" className="btn-secondary" onClick={onClose}>
            Cancelar
          </button>
          <button
            type="submit"
            form="nova-movimentacao-form"
            className="btn-primary"
            disabled={criarMovimentacao.isPending || !podeSubmeter}
          >
            {criarMovimentacao.isPending ? 'Criando...' : 'Criar Movimentação'}
          </button>
        </div>
      }
    >
      <form id="nova-movimentacao-form" onSubmit={handleSubmit} className="space-y-4 py-4">
        <div>
          <label className="block text-sm font-medium text-gray-700">Fluxo</label>
          <select
            className="input-gaveta mt-1 w-full"
            value={tipoFluxo}
            onChange={(event) => setTipoFluxo(event.target.value as TipoFluxoMovimentacao)}
          >
            {Object.entries(TIPO_FLUXO_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </div>

        {ehDevolucao ? (
          <div className="space-y-2">
            <p className="text-sm font-medium text-gray-700">Equipamento(s) a devolver *</p>
            {equipamentosColaborador.length === 0 && (
              <p className="text-sm text-gray-500">Nenhum equipamento vinculado a este colaborador no Inventário.</p>
            )}
            {equipamentosColaborador.length === 1 && (
              <ListaEquipamentosColaborador equipamentos={equipamentosColaborador} />
            )}
            {equipamentosColaborador.length > 1 && (
              <ListaEquipamentosColaborador
                equipamentos={equipamentosColaborador}
                selecionados={escolhidosIds}
                onToggle={(id) =>
                  setEscolhidosIds((atual) => {
                    const novo = new Set(atual);
                    if (novo.has(id)) novo.delete(id);
                    else novo.add(id);
                    return novo;
                  })
                }
              />
            )}
            <label className="block text-sm font-medium text-gray-700">
              Outro equipamento que não está na lista?
              <input
                type="text"
                className="input-gaveta mt-1 w-full"
                placeholder="Descreva o equipamento"
                value={outroEquipamento}
                onChange={(event) => setOutroEquipamento(event.target.value)}
              />
            </label>
          </div>
        ) : (
        <>
        <div>
          <label className="block text-sm font-medium text-gray-700">Tipo de equipamento *</label>
          <select
            className="input-gaveta mt-1 w-full"
            value={snapshotTipoEquipamento}
            onChange={(event) => {
              setSnapshotTipoEquipamento(event.target.value as TipoEquipamentoMovimentacao);
              setSistemaOperacional('');
              setHomologado(false);
            }}
          >
            {Object.entries(TIPO_EQUIPAMENTO_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </div>

        {soVisivel && (
          <div>
            <label className="block text-sm font-medium text-gray-700">Sistema Operacional *</label>
            <select
              className="input-gaveta mt-1 w-full"
              value={sistemaOperacional}
              onChange={(event) => setSistemaOperacional(event.target.value as SistemaOperacionalMovimentacao)}
            >
              <option value="">Selecione...</option>
              {(soOpcoes ?? []).map((value) => (
                <option key={value} value={value}>
                  {SISTEMA_OPERACIONAL_LABELS[value]}
                </option>
              ))}
            </select>
          </div>
        )}

        {homologadoVisivel && (
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
              checked={homologado}
              onChange={(event) => setHomologado(event.target.checked)}
            />
            Equipamento homologado
          </label>
        )}

        </>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700">Cliente</label>
          <ClientCombobox
            selectedId={cliente?.id ?? null}
            onChange={(client) => setCliente(client ? { id: client.id, name: client.name } : null)}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">{ehDevolucao ? 'Data prevista para devolução' : 'Data prevista'}</label>
          <input
            type="date"
            className="input-gaveta mt-1 w-full"
            value={dataPrevista}
            onChange={(event) => setDataPrevista(event.target.value)}
          />
        </div>

        <p className="text-xs text-gray-500">* Campos obrigatórios</p>
      </form>
    </BaseModal>
  );
}
